<template>
	<div
		v-if="subcategory.isVisible"
		class="card"
		:class="{ 'card--danger': subcategory.isMandatory }"
	>
		<div
			class="image-wrapper"
			:class="{
				'no-image': subcategory.showMontlyCost && !subcategory.imageBase64
			}"
		>
			<div v-if="subcategory.showMontlyCost" class="cost-summary">
				{{ subcategory.cost | currency }}
			</div>
			<img
				v-if="subcategory.imageBase64"
				:src="subcategory.imageBase64"
				alt=""
			/>
		</div>
		<div class="shopping-content">
			<div class="heading-2 name">{{ subcategory.name }}</div>
			<div v-if="subcategory.description" class="body-text-1 description">
				{{ subcategory.description }}
				<div v-if="loanInfo" class="loan-msg">
					{{ loanInfo }}
				</div>
			</div>
			<div v-if="subcategory.isMandatory" class="heading-5">
				{{ $t('isMandatory') }}
			</div>
			<div
				v-if="adultsCount > 0 || childrenCount > 0"
				class="household-summary"
			>
				<div
					v-if="adultsCount > 0 && isPricePerHouseholdMember"
					class="body-text-2"
					:aria-label="adultsAriaLabel"
				>
					<div class="household-msg" aria-hidden="true">
						{{
							$t('shoppingCostPerAdult', {
								count: adultsCount,
								cost: $options.filters.currency(subcategory.cost)
							})
						}}
					</div>
					<div aria-hidden="true">
						{{ totalAdultCost | currency }}
					</div>
				</div>
				<div
					v-if="childrenCount > 0 && hasChildPrice"
					class="body-text-2"
					:aria-label="childrenAriaLabel"
				>
					<div class="household-msg" aria-hidden="true">
						{{
							$t('shoppingCostPerChild', {
								count: childrenCount,
								cost: $options.filters.currency(subcategory.costPerChild)
							})
						}}
					</div>
					<div aria-hidden="true">
						{{ totalChildrenCost | currency }}
					</div>
				</div>
			</div>
			<div class="heading-1 cost">
				{{ totalCost | currency }}
			</div>
			<div class="button-wrapper" :class="{ 'flex-right': previewOnly }">
				<ja-button
					v-if="!previewOnly"
					class="small outlined"
					:success="cardActive"
					:disabled="disabled"
					:name="`select${subcategory.name}`"
					:aria-label="buttonAriaLabel"
					@click.native="toggleSelectButton"
				>
					{{ $t('select') }}
				</ja-button>
				<div v-if="subcategory.canChooseQuantity" class="dropdown-wrapper">
					<ja-dropdown
						v-model="currentQuantityOption"
						class="shopping-dropdown"
						:name="`shopping-dropdown-${subcategory.name}`"
						:accessibility-label="$t('quantity')"
						:options="quantityOptions"
						:disabled="disabled"
					></ja-dropdown>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import Dropdown from '../ui/form/Dropdown';
import Button from '../ui/Button';
import { defaultNames } from '@/helpers/constants';

export default {
	components: {
		jaDropdown: Dropdown,
		jaButton: Button
	},
	props: {
		subcategory: {
			type: Object,
			default: null
		},
		childrenCount: {
			type: Number,
			default: 0
		},
		adultsCount: {
			type: Number,
			default: 0
		},
		loanInfo: {
			type: String,
			default: null
		},
		data: {
			type: Object,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		previewOnly: {
			type: Boolean,
			default: false
		},
		selectCard: {
			type: Boolean,
			default: false
		},
		purchasedQuantities: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			cardActive: false,
			maxQuantity: 5,
			currentQuantityOption: 0,
			selection: {}
		};
	},
	computed: {
		buttonAriaLabel() {
			const mainText = this.$t('selectShoppingItem', {
				item: this.subcategory.name,
				value: this.totalCost
			});

			if (this.subcategory.isMandatory) {
				return `${mainText}; ${this.$t('requiredCategory')}`;
			}

			return mainText;
		},
		totalAdultCost() {
			return this.adultsCount * this.subcategory.cost;
		},
		totalChildrenCost() {
			return this.childrenCount * this.subcategory.costPerChild;
		},
		adultsAriaLabel() {
			return this.$t('costPerAdult', {
				count: this.adultsCount,
				cost: this.subcategory.cost,
				total: this.totalAdultCost
			});
		},
		childrenAriaLabel() {
			return this.$t('costPerChild', {
				count: this.childrenCount,
				cost: this.subcategory.costPerChild,
				total: this.totalChildrenCost
			});
		},

		isPricePerHouseholdMember() {
			return this.hasAdultPrice && this.hasChildPrice;
		},
		hasAdultPrice() {
			return (
				typeof this.subcategory.cost == 'number' && this.subcategory.cost > 0
			);
		},
		hasChildPrice() {
			return (
				typeof this.subcategory.costPerChild == 'number' &&
				this.subcategory.costPerChild > 0
			);
		},
		selectedQuantity() {
			return this.quantityOptions[this.currentQuantityOption].value;
		},
		totalCost() {
			if (this.subcategory.showMontlyCost) {
				if (this.subcategory.interestAutoLoan === defaultNames.AUTO_LOAN) {
					return (
						this.subcategory.monthlyLoanAmount + this.subcategory.additionalCost
					);
				} else if (
					this.subcategory.interestAutoLoan ===
					defaultNames.AUTO_LOAN_DEALERSHIP
				) {
					return (
						this.subcategory.monthlyLoanDealershipAmount +
						this.subcategory.additionalCost
					);
				} else {
					return (
						this.subcategory.monthlyLoanAmount + this.subcategory.additionalCost
					);
				}
			}
			if (this.isPricePerHouseholdMember) {
				return (
					this.adultsCount * this.subcategory.cost * this.selectedQuantity +
					this.childrenCount *
						this.subcategory.costPerChild *
						this.selectedQuantity
				);
			} else {
				if (this.hasAdultPrice) {
					return this.subcategory.cost * this.selectedQuantity;
				} else if (this.hasChildPrice) {
					return this.subcategory.costPerChild * this.selectedQuantity;
				} else {
					return 0;
				}
			}
		},
		quantityOptions() {
			return new Array(this.maxQuantity).fill().map((value, index) => {
				return { name: index + 1, value: index + 1 };
			});
		},
		quantityOptionsWithValues() {
			return this.quantityOptions.map(option => {
				const selectedQuantity = option.value;
				const totalCost =
					this.adultsCount * this.subcategory.cost * selectedQuantity +
					this.childrenCount * this.subcategory.costPerChild * selectedQuantity;

				return {
					adultQuantity: 2,
					childQuantity: this.childrenCount,
					totalCost,
					selectedQuantity
				};
			});
		}
	},
	watch: {
		data(newValue) {
			// Used when remove from Payment Component
			// TODO: quick fix - the whole component should be updated
			if (!newValue && this.cardActive) {
				this.toggleSelectButton();
			}
		},
		// Used when a category is already purchased
		selectCard(a) {
			if (a) {
				this.toggleSelectButton();
			}
		},
		currentQuantityOption(newVal) {
			this.currentQuantity = this.quantityOptions[newVal].value;
			this.updateSelection();
			this.$emit('subcategorySelected', this.selection);
		},
		totalCost(newTotalCost, oldTotalCost) {
			if (newTotalCost !== oldTotalCost && this.selection.isSelected) {
				this.updateSelection();
				this.$emit('subcategorySelected', this.selection);
			}
		}
	},
	created() {
		// Watch "purchasedQuantities" only once per instance
		// If it set as a regular Watcher it causes problems
		// with qty updates (component should be refactored).
		// Also it triggeres the method serval times which is not ok.
		this.unwatchPurchasesQantities = this.$watch(
			'purchasedQuantities',
			newVal => {
				if (newVal) {
					this.currentQuantityOption = this.getCurrentQuantityOption();

					this.unwatchPurchasesQantities();
				}
			}
		);
	},
	mounted() {
		if (this.data) {
			this.selection = this.data;
			this.cardActive = true;
			this.currentQuantityOption = this.getCurrentQuantityOption();
		}

		if (this.subcategory.isSelected) {
			this.updateSelection();
			this.$emit('subcategorySelected', this.selection);
		}
	},
	methods: {
		getCurrentQuantityOption() {
			if (this.purchasedQuantities.adultQty) {
				return this.quantityOptions.findIndex(
					i => i.value === this.purchasedQuantities.selectionCount
				);
			}

			if (this.data && this.data.adultQuantity) {
				const index = this.quantityOptions.findIndex(item => {
					return this.data.adultQuantity == item.value;
				});

				if (index > -1) {
					return index;
				}
			}
			return 0;
		},
		updateSelection() {
			if (!this.selection) {
				this.selection = {};
			}
			this.selection.expenseSubCategory_ID = this.subcategory.expenseSubCategory_ID;
			this.selection.isMandatory = this.subcategory.isMandatory;
			this.selection.name = this.subcategory.name;
			this.selection.totalCost = this.totalCost;
			this.selection.cost = this.subcategory.cost;
			this.selection.monthlyLoanAmount = this.subcategory.monthlyLoanAmount;
			this.selection.monthlyLoanDealershipAmount = this.subcategory.monthyLoanDealershipAmount;

			if (this.isPricePerHouseholdMember) {
				this.selection.adultQuantity = this.selectedQuantity * this.adultsCount;
				this.selection.childQuantity =
					this.selectedQuantity * this.childrenCount;
			} else {
				this.selection.adultQuantity = this.hasAdultPrice
					? this.selectedQuantity
					: null;
				this.selection.childQuantity = this.hasChildPrice
					? this.selectedQuantity
					: null;
			}
		},
		toggleSelectButton() {
			if (this.cardActive) {
				this.selection.isSelected = false;
				this.$emit('subcategoryDeselected', this.selection);
			} else {
				this.selection.isSelected = true;
				this.updateSelection();
				this.$emit('subcategorySelected', this.selection);
			}
			this.cardActive = !this.cardActive;
		}
	}
};
</script>
<style lang="scss" scoped>
.card {
	font-family: 'RobotoRegular';
	font-size: 15px;
	background-color: $container-bgcolor;
	color: $questionnaire-color;
	border: 1px solid $container-border;
	border-radius: 8px;
	box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
	margin-bottom: 16px;

	&--danger {
		border: 2px solid $font-red-alert;
	}

	.image-wrapper {
		padding: 8px 8px 0 8px;
		position: relative;
		height: 226px;

		&.no-image {
			padding-bottom: 56px;
			height: 0px;

			img {
				height: 100%;
				width: 100%;
			}
		}

		img {
			margin: auto;
			display: block;
			width: 226px;
			height: 226px;
		}

		.cost-summary {
			position: absolute;
			height: 44px;
			width: 173px;
			border-radius: 0 0 100px 0;
			background-color: $primary-state;
			color: $font-light;
			padding: 16px 0 0 24px;
		}
	}

	.shopping-content {
		padding: 16px 24px;

		.heading-5 {
			text-transform: uppercase;
			color: $requiredFieldAsterisk;
			margin-top: 8px;
		}

		.heading-2 {
			margin: 0 0 4px 0;
		}

		.heading-1 {
			margin: 16px 0;
			color: $font-blue;
		}

		.heading-3 {
			margin-top: 0px;
			margin-bottom: 16px;
		}

		.loan-msg {
			white-space: pre-line;
		}

		.household-summary {
			margin-top: 16px;

			.body-text-2 {
				display: flex;

				&:first-child {
					margin-bottom: 4px;
				}

				.household-msg {
					flex-grow: 1;
				}
			}
		}

		.button-wrapper {
			display: flex;
			align-items: center;
			justify-content: space-between;
			&.flex-right {
				justify-content: flex-end;
			}
			.dropdown-wrapper {
				width: 78px;
			}
		}
	}
}
</style>
